import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import './StudentsLifeSection.css';
import TrainingEventCalendar from './TrainingEventCalendar';

const API_BASE = 'https://preprod.api.clevory.com';
const EVENTS_API_URL = `${API_BASE}/events/clevoryacademy`;
const CALENDAR_STORAGE_KEY = 'clevory_course_calendar_payload';

function normalizeText(value) {
	if (Array.isArray(value)) {
		return value
			.map((item) =>
				typeof item === 'string' ? item.trim() : String(item || '').trim(),
			)
			.filter(Boolean)
			.join('\n\n');
	}

	if (typeof value === 'string') return value.trim();
	if (value == null) return '';
	return String(value).trim();
}

function isHtmlContent(value) {
	if (typeof value !== 'string') return false;
	return /<\/?[a-z][\s\S]*>/i.test(value);
}

function normalizeName(value) {
	return String(value || '')
		.trim()
		.toLowerCase()
		.replace(/\s+/g, ' ');
}

function formatDate(dateString) {
	if (!dateString) return '';

	const date = new Date(String(dateString).replace(' ', 'T'));
	if (Number.isNaN(date.getTime())) return '';

	return new Intl.DateTimeFormat('en-US', {
		month: 'short',
		day: '2-digit',
		year: 'numeric',
	}).format(date);
}

function mapCourseCategoriesToModules(courseData) {
	if (!Array.isArray(courseData?.categories)) return [];

	return courseData.categories.map((category, index) => ({
		id: category.id || `category-${index + 1}`,
		title: category.name || `Module ${index + 1}`,
		content:
			normalizeText(category.description) ||
			normalizeText(category.elements) ||
			normalizeText(category.content) ||
			normalizeText(category.html_content) ||
			'Aucun contenu disponible',
	}));
}

function mapEventToTrainingItem(item, fallbackIndex = 0) {
	return {
		id: item.id ?? `event-${fallbackIndex + 1}`,
		courseid: item.courseid ?? null,
		title: item.title || item.name || 'Untitled Training',
		name: item.name || item.title || 'Untitled Training',
		date: formatDate(item.startdate || item.start),
		category:
			Array.isArray(item.tag_ids) && item.tag_ids.length > 0
				? item.tag_ids[0]
				: item.category || 'Training',
		description: item.description || '',
		address: item.address || item.location || '',
		location: item.address || item.location || '',
		startdate: item.startdate || item.start || '',
		enddate: item.enddate || item.end || '',
		start: item.startdate || item.start || '',
		end: item.enddate || item.end || '',
		image: item.image || '',
		allDay:
			item.allDay ||
			(!String(item.startdate || item.start || '').includes('T') &&
				!String(item.enddate || item.end || '').includes('T')),
	};
}

function saveCalendarPayload(payload) {
	try {
		sessionStorage.setItem(CALENDAR_STORAGE_KEY, JSON.stringify(payload));
	} catch (error) {
		console.error(
			'Impossible de sauvegarder le calendrier en sessionStorage:',
			error,
		);
	}
}

export default function StudentsLifeSection({
	onViewCalendar,
	training,
	allTrainings = [],
}) {
	const navigate = useNavigate();

	const newsletterBackground =
		"url('/assets/images/school/students/students.jpg')";

	const [modules, setModules] = useState([]);
	const [loadingModules, setLoadingModules] = useState(false);
	const [openModuleIndex, setOpenModuleIndex] = useState(null);

	const [relatedTrainings, setRelatedTrainings] = useState([]);
	const [loadingRelatedEvents, setLoadingRelatedEvents] = useState(false);

	const courseId = training?.courseid?.id ?? training?.courseid ?? null;

	const currentTrainingName = useMemo(
		() => normalizeName(training?.title || training?.name),
		[training?.title, training?.name],
	);

	useEffect(() => {
		let isMounted = true;

		if (!courseId) {
			setModules([]);
			setOpenModuleIndex(null);
			return;
		}

		const fetchCourse = async () => {
			setLoadingModules(true);

			try {
				const response = await fetch(`${API_BASE}/course/getid/${courseId}`, {
					headers: { Accept: 'application/json' },
				});

				if (!response.ok) {
					throw new Error(`HTTP ${response.status}`);
				}

				const data = await response.json();
				const nextModules = mapCourseCategoriesToModules(data);

				if (isMounted) {
					setModules(nextModules);
					setOpenModuleIndex(null);
				}
			} catch (error) {
				console.error('Erreur chargement du cours:', error);

				if (isMounted) {
					setModules([]);
					setOpenModuleIndex(null);
				}
			} finally {
				if (isMounted) setLoadingModules(false);
			}
		};

		fetchCourse();

		return () => {
			isMounted = false;
		};
	}, [courseId]);

	useEffect(() => {
		let isMounted = true;

		if (!training || !currentTrainingName) {
			setRelatedTrainings([]);
			return;
		}

		const fetchRelatedEvents = async () => {
			setLoadingRelatedEvents(true);

			try {
				const response = await fetch(EVENTS_API_URL, {
					headers: { Accept: 'application/json' },
				});

				if (!response.ok) {
					throw new Error(`HTTP ${response.status}`);
				}

				const events = await response.json();

				if (!Array.isArray(events)) {
					throw new Error('Events API did not return an array');
				}

				const sameNameEvents = events
					.filter((eventItem) => {
						const eventName = normalizeName(
							eventItem?.name || eventItem?.title,
						);

						return eventName && eventName === currentTrainingName;
					})
					.map((eventItem, index) => mapEventToTrainingItem(eventItem, index));

				const currentMapped = mapEventToTrainingItem(training, -1);

				const mergedSource = sameNameEvents.length
					? [...sameNameEvents, currentMapped]
					: [currentMapped];

				const merged = mergedSource.reduce((acc, item) => {
					const key = `${item.id}-${item.startdate}-${item.title}`;

					if (!acc.some((existing) => existing.__key === key)) {
						acc.push({ ...item, __key: key });
					}

					return acc;
				}, []);

				const cleaned = merged
					.map(({ __key, ...item }) => item)
					.sort((a, b) => {
						const aDate = new Date(
							String(a.startdate || '').replace(' ', 'T'),
						).getTime();

						const bDate = new Date(
							String(b.startdate || '').replace(' ', 'T'),
						).getTime();

						if (Number.isNaN(aDate) && Number.isNaN(bDate)) return 0;
						if (Number.isNaN(aDate)) return 1;
						if (Number.isNaN(bDate)) return -1;

						return aDate - bDate;
					});

				if (isMounted) {
					setRelatedTrainings(cleaned);
				}
			} catch (error) {
				console.error('Erreur chargement des événements liés:', error);

				if (isMounted) {
					setRelatedTrainings([mapEventToTrainingItem(training, -1)]);
				}
			} finally {
				if (isMounted) setLoadingRelatedEvents(false);
			}
		};

		fetchRelatedEvents();

		return () => {
			isMounted = false;
		};
	}, [training, currentTrainingName]);

	const calendarTrainings =
		relatedTrainings.length > 0
			? relatedTrainings
			: allTrainings?.length > 0
				? allTrainings
						.filter((item) => {
							const itemName = normalizeName(item?.title || item?.name);
							return itemName && itemName === currentTrainingName;
						})
						.map((item, index) => mapEventToTrainingItem(item, index))
				: training
					? [mapEventToTrainingItem(training, -1)]
					: [];

	const handleToggleModule = (index) => {
		setOpenModuleIndex((prev) => (prev === index ? null : index));
	};

	const handleOpenFullCalendar = () => {
		const payload = {
			source: 'students-life-specific-training',
			title: training?.title || training?.name || 'Calendrier des formations',
			current: training || null,
			events: calendarTrainings,
			timestamp: Date.now(),
		};

		saveCalendarPayload(payload);

		if (typeof onViewCalendar === 'function') {
			onViewCalendar(payload);
			return;
		}

		navigate('/calendrier?open=calendar#course-calendar-section', {
			state: {
				openCalendar: true,
				calendarEvents: calendarTrainings,
				current: training || null,
				title: payload.title,
				source: payload.source,
			},
		});
	};

	return (
		<section className='students-life'>
			<div className='students-life__container'>
				<div className='students-life__header'>
					<h2 className='students-life__title'>
						{training?.title || training?.name || 'Formation'}
					</h2>

					{training && (
						<p className='students-life__meta students-life__meta--top'>
							{training.date} | {training.category}
						</p>
					)}
				</div>

				<div className='students-life__grid'>
					<div className='students-life__content'>
						<div className='students-life__content-card'>
							{training?.description && (
								<p className='training_desc'>{training.description}</p>
							)}

							<div className='students-life__list'>
								{loadingModules ? (
									<p className='students-life__loading'>
										Chargement des modules...
									</p>
								) : modules.length ? (
									modules.map((module, index) => {
										const isOpen = openModuleIndex === index;
										const hasHtml = isHtmlContent(module.content);

										return (
											<article
												className={`students-life__item students-life__item--accordion ${
													isOpen ? 'students-life__item--open' : ''
												}`}
												key={module.id}>
												<button
													type='button'
													className='students-life__accordion-trigger'
													onClick={() => handleToggleModule(index)}
													aria-expanded={isOpen}>
													<div className='students-life__module-badge'>
														<span className='students-life__module-badge-label'>
															MOD
														</span>

														<strong className='students-life__module-badge-number'>
															{index + 1}
														</strong>
													</div>

													<div className='students-life__accordion-body'>
														<h3 className='students-life__event-title'>
															{module.title}
														</h3>
													</div>

													<span
														className={`students-life__accordion-icon ${
															isOpen ? 'open' : ''
														}`}>
														<ChevronDown size={18} />
													</span>
												</button>

												<div
													className={`students-life__accordion-content ${
														isOpen ? 'open' : ''
													}`}>
													<div className='students-life__accordion-inner'>
														{hasHtml ? (
															<div
																className='students-life__module-richtext'
																dangerouslySetInnerHTML={{
																	__html: module.content,
																}}
															/>
														) : (
															<p className='students-life__module-text'>
																{module.content}
															</p>
														)}
													</div>
												</div>
											</article>
										);
									})
								) : (
									<p className='students-life__empty'>
										Aucun module trouvé pour ce cours
									</p>
								)}
							</div>
						</div>
					</div>

					<div className='students-life__aside'>
						<aside
							className='students-life__newsletter'
							style={{ backgroundImage: newsletterBackground }}>
							<div className='students-life__newsletter-overlay' />

							<div className='students-life__newsletter-content'>
								<p className='students-life__newsletter-eyebrow'>Programme</p>

								<h3 className='students-life__newsletter-title'>
									Ce que vous allez
									<br />
									<span>apprendre</span>
								</h3>

								<p className='students-life__newsletter-text'>
									Consultez les prochaines sessions directement dans le
									calendrier interactif puis explorez les modules du parcours
									sélectionné.
								</p>

								{loadingRelatedEvents && (
									<p className='students-life__related-status'>
										Chargement des autres sessions...
									</p>
								)}
							</div>
						</aside>

						<div className='students-life__calendar-wrap'>
							<TrainingEventCalendar
								currentTraining={training}
								allTrainings={calendarTrainings}
								compact
								nextCount={3}
							/>

							<button
								type='button'
								className='students-life__calendar-btn'
								onClick={handleOpenFullCalendar}>
								<span className='students-life__calendar-btn-fill' />
								<span className='students-life__calendar-btn-text'>
									Voir le calendrier complet
								</span>
							</button>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
